/**
 * OfflineStorage - IndexedDB-backed storage for offline data persistence.
 *
 * Stores pending form submissions (work orders, inspections, time logs, photos)
 * that were created while offline, to be synced when connectivity returns.
 *
 * Database: 'gb-offline-db', version 1
 * Stores:
 *	 - pendingWorkOrders: work orders created offline
 *	 - pendingInspections: inspections created offline
 *	 - pendingTimeLogs: time log entries added offline
 *	 - pendingPhotos: photos captured offline (stores metadata + base64 data)
 *	 - pendingUpdates: status updates, notes, etc. made offline
 *
 * Each record in every store gets a unique auto-incremented `id` key and a
 * `createdAt` ISO timestamp so items can be synced in chronological order.
 *
 * Uses the `idb` library for a promise-based IndexedDB API.
 */

import { openDB, IDBPDatabase } from 'idb';

// --- Database configuration ---

/** Name of the IndexedDB database */
const DB_NAME = 'gb-offline-db';

/** Current schema version — increment when adding/changing stores */
const DB_VERSION = 2;

/**
 * All object store names used for pending offline data.
 * Each store holds items of a specific type awaiting sync.
 */
export const STORE_NAMES = {
	WORK_ORDERS: 'pendingWorkOrders',
	INSPECTIONS: 'pendingInspections',
	TIME_LOGS: 'pendingTimeLogs',
	PHOTOS: 'pendingPhotos',
	UPDATES: 'pendingUpdates',
	/** Stores checklist item toggles made while offline */
	CHECKLIST_UPDATES: 'pendingChecklistUpdates',
	/** Stores full checklist state snapshots for offline resume */
	CHECKLIST_SNAPSHOTS: 'checklistSnapshots',
} as const;

/** Union type of all valid store name values */
export type StoreName = typeof STORE_NAMES[keyof typeof STORE_NAMES];

/**
 * Shape of every record stored in the pending stores.
 *
 * @property id		   - Auto-incremented primary key (assigned by IndexedDB)
 * @property createdAt - ISO 8601 timestamp of when the item was queued offline
 * @property data	   - The actual payload (work order form data, photo base64, etc.)
 */
export interface PendingItem {
	id?: number;
	createdAt: string;
	data: Record<string, unknown>;
}

// --- Singleton database instance ---

/** Cached DB connection so we don't re-open on every call */
let dbInstance: IDBPDatabase | null = null;

/**
 * initDB() - Opens (or creates) the IndexedDB database.
 *
 * On first run (or version upgrade), the `upgrade` callback creates all
 * five object stores with auto-incrementing primary keys. The `createdAt`
 * field is indexed so items can be queried in chronological order.
 *
 * Returns a cached connection on subsequent calls to avoid unnecessary
 * re-opens (IndexedDB connections are long-lived).
 *
 * @returns The opened IDBPDatabase instance
 */
export async function initDB(): Promise<IDBPDatabase> {
	// Return cached connection if already open
	if (dbInstance) {
		return dbInstance;
	}

	dbInstance = await openDB(DB_NAME, DB_VERSION, {
		/**
		 * upgrade() runs when the DB is first created or when DB_VERSION
		 * is higher than the existing version. This is where we define
		 * the schema (object stores and indexes).
		 */
		upgrade(db) {
			// Iterate over all store names and create each one
			const storeNames = Object.values(STORE_NAMES);
			for (const storeName of storeNames) {
				// Skip stores that already exist (in case of incremental upgrades)
				if (!db.objectStoreNames.contains(storeName)) {
					// Checklist snapshots use workOrderId as key (no auto-increment)
					// so we can overwrite snapshots by ID with put()
					const isSnapshotStore = storeName === STORE_NAMES.CHECKLIST_SNAPSHOTS;
					const store = db.createObjectStore(storeName, {
						keyPath: 'id',
						autoIncrement: !isSnapshotStore,
					});

					// Index on createdAt for chronological queries
					store.createIndex('createdAt', 'createdAt', { unique: false });
				}
			}
		},
	});

	return dbInstance;
}

/**
 * addPendingItem() - Adds a new item to the specified pending store.
 *
 * Automatically stamps the record with the current timestamp in the
 * `createdAt` field. The `id` is auto-generated by IndexedDB.
 *
 * @param storeName - Which pending store to write to (e.g., STORE_NAMES.WORK_ORDERS)
 * @param data		- The payload to store (form data, photo metadata, etc.)
 * @returns The auto-generated numeric id of the new record
 */
export async function addPendingItem(
	storeName: StoreName,
	data: Record<string, unknown>
): Promise<number> {
	const db = await initDB();

	// Build the record with a creation timestamp
	const item: PendingItem = {
		createdAt: new Date().toISOString(),
		data,
	};

	// Add to the store and return the generated key (numeric id)
	const id = await db.add(storeName, item);
	return id as number;
}

/**
 * getPendingItems() - Retrieves all items from a given pending store.
 *
 * Items are returned in insertion order (by auto-incremented id).
 * The caller can sort by `createdAt` if chronological order is needed.
 *
 * @param storeName - Which pending store to read from
 * @returns Array of all PendingItem records in the store
 */
export async function getPendingItems(
	storeName: StoreName
): Promise<PendingItem[]> {
	const db = await initDB();

	// getAll() returns every record in the store
	const items = await db.getAll(storeName);
	return items as PendingItem[];
}

/**
 * removePendingItem() - Deletes a single item from a pending store by id.
 *
 * Called after an item has been successfully synced to the server, so it
 * won't be re-sent on the next sync attempt.
 *
 * @param storeName - Which pending store to delete from
 * @param id		- The numeric id of the record to remove
 */
export async function removePendingItem(
	storeName: StoreName,
	id: number
): Promise<void> {
	const db = await initDB();
	await db.delete(storeName, id);
}

/**
 * getPendingCount() - Returns the total count of pending items across ALL stores.
 *
 * Used to display the badge/counter in the SyncStatusBar so the user knows
 * how many offline changes are waiting to be synced.
 *
 * @returns Total number of pending items across all five stores
 */
export async function getPendingCount(): Promise<number> {
	const db = await initDB();

	let totalCount = 0;
	const storeNames = Object.values(STORE_NAMES);

	// Sum up the count from each store
	for (const storeName of storeNames) {
		// Snapshots are cached checklist state used to resume the workflow
		// offline — they are NOT items awaiting sync. Counting them would
		// keep the "Sync Now" banner stuck on screen forever (one snapshot
		// per viewed work order, never drained by syncAll()).
		if (storeName === STORE_NAMES.CHECKLIST_SNAPSHOTS) continue;
		const count = await db.count(storeName);
		totalCount += count;
	}

	return totalCount;
}

/**
 * saveChecklistSnapshot() - Saves or updates a checklist state snapshot for offline use.
 *
 * Uses the workOrderId as the key so there's only one snapshot per work order.
 * Called after every checklist fetch or local toggle so the user can resume
 * their workflow even when offline.
 *
 * @param workOrderId - The work order this checklist belongs to
 * @param data        - The full checklist state to persist
 */
export async function saveChecklistSnapshot(
	workOrderId: string,
	data: Record<string, unknown>
): Promise<void> {
	const db = await initDB();
	const record = {
		id: workOrderId,
		createdAt: new Date().toISOString(),
		data,
	};
	// put() will insert or update based on the key
	await db.put(STORE_NAMES.CHECKLIST_SNAPSHOTS, record);
}

/**
 * getChecklistSnapshot() - Retrieves a cached checklist snapshot for a work order.
 *
 * Returns null if no snapshot exists (first time opening the workflow online).
 *
 * @param workOrderId - The work order to look up
 * @returns The cached checklist data, or null
 */
export async function getChecklistSnapshot(
	workOrderId: string
): Promise<Record<string, unknown> | null> {
	const db = await initDB();
	const record = await db.get(STORE_NAMES.CHECKLIST_SNAPSHOTS, workOrderId);
	return record ? (record as PendingItem).data : null;
}

/**
 * clearAll() - Removes all records from all pending stores.
 *
 * Called after a successful full sync to reset the offline queue.
 * Uses a single transaction spanning all stores for atomicity —
 * either everything is cleared or nothing is.
 */
export async function clearAll(): Promise<void> {
	const db = await initDB();

	const storeNames = Object.values(STORE_NAMES);

	// Clear each store individually (idb doesn't support multi-store clear in one tx easily)
	for (const storeName of storeNames) {
		await db.clear(storeName);
	}
}
